import React from 'react';
import { client } from 'src/utils/api-client';

import { Autocomplete, TextFieldProps } from '@mui/material';
import { GpcCodeOutSchema } from '../../types';
import { useQuery } from 'react-query';
import { StyledTextField } from '../steps/productInformation';

interface GpcCodeAutocompleteProps {
  value: string | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (code: GpcCodeOutSchema | null) => void;
  inputProps: TextFieldProps['InputProps'];
}

type ObjMap = { [key: string]: GpcCodeOutSchema };

export default function GpcCodeAutocomplete(props: GpcCodeAutocompleteProps) {
  const { value, onChange } = props;
  // searchTimeout to fetch delayed data from the API
  const [searchTimeout, setSearchTimeout] = React.useState<any>(null);

  // full ecoinvent activity objects fetched from the API
  // the new search does not override the existing data, it is merged
  // options are filtered by searchText
  const [data, setData] = React.useState<ObjMap>({});

  // searchText captured from Autocomplete (user inputs) for options filtering
  const [searchText, setSearchText] = React.useState('');

  const { data: currentSelection } = useQuery(
    ['gpc', { value }],
    () =>
      client
        .get<GpcCodeOutSchema>(`/web/lca/product-codes/gpc/${value}`)
        .then((res) => res.data),
    {
      enabled: !!value,
    }
  );

  const getData = (searchText: string) => {
    // fetch data from the API
    client
      .get<GpcCodeOutSchema[]>(
        `/web/lca/product-codes/gpc?text=${searchText}&limit=250`
      )
      .then((res) => {
        // inject the object assigned activity if already selected
        const newData: GpcCodeOutSchema[] = currentSelection
          ? [currentSelection, ...res.data]
          : res.data;

        // create a map of the new data
        const newDataMap: ObjMap = {};
        newData.forEach((item) => {
          newDataMap[item.code] = item;
        });

        // set the new data
        setData(newDataMap);
      });
  };

  // handle search input change
  // fetch data from the API after 1 second of inactivity
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchText(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      getData(value);
    }, 1000);
    setSearchTimeout(timeout);
  };

  // fetch data from the API on component mount
  React.useEffect(() => {
    getData('');
  }, []);

  // if the currentSelection changes, update the data
  React.useEffect(() => {
    if (currentSelection) {
      setData((prevData) => {
        return {
          ...prevData,
          [currentSelection.code]: currentSelection,
        };
      });
    }
  }, [currentSelection]);

  const handleOnChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: any
  ) => {
    // get the activity from the data array
    // and send it to the parent component
    const activity = data[value] || null;
    onChange(activity);
  };

  const getOptionLabel = (option: GpcCodeOutSchema | undefined) =>
    option?.text || '';

  // data parsed to { value,label } array for MuiAutocomplete
  const options = Object.values(data).map((code: GpcCodeOutSchema) => ({
    value: code.code,
    label: getOptionLabel(code),
  }));

  // searchText filtered and parsed to value array for MuiAutocomplete
  const _options = (options || [])
    .filter((item) =>
      item.label.toLowerCase().includes(searchText.toLowerCase())
    )
    .map((item) => item.value);

  return (
    <Autocomplete
      fullWidth
      options={_options}
      value={value}
      onChange={handleOnChange}
      sx={{ backgroundColor: !!value ? '#F6F6F6' : 'inherit' }} // I couldn't force it with themes styling
      renderInput={(params) => (
        <StyledTextField
          {...{
            ...params,
            InputProps: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {props.inputProps?.endAdornment}
                  {params.InputProps?.endAdornment}
                </>
              ),
            },
          }}
          value=""
          label="GPC Code (optional)"
          onChange={handleSearchChange}
        />
      )}
      getOptionLabel={(value) => getOptionLabel(data[value])}
    />
  );
}
